import {
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { tokens } from '../../../../global/theme/tokens';
import { DashedRow, SectionLabel } from './taskCardUi';

// externalResources of a task
const TaskCardLinks = ({ resources = [], onLinkAdd, onLinkDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isAdding, setIsAdding] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '' });

  function handleClose() {
    setNewLink({ label: '', url: '' });
    setIsAdding(false);
  }

  // submitted by the button and by Enter
  function handleSubmit() {
    const url = newLink.url.trim();
    if (!url) return handleClose();

    if (onLinkAdd) onLinkAdd({ label: newLink.label.trim() || url, url });
    handleClose();
  }

  return (
    <Box className="flex flex-col gap-2">
      <SectionLabel>{t('Links')}</SectionLabel>
      <Box className="flex flex-col gap-2">
        {resources.map((resource) => (
          <Box
            key={resource.id}
            className="flex items-center gap-1 rounded-lg pl-1 pr-3 py-1"
            sx={{
              backgroundColor: colors.bgInput,
              border: '1px solid transparent',
              ':hover': { border: `1px solid ${colors.info}60` },
            }}
          >
            <Box
              component="a"
              href={resource.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 grow min-w-0 no-underline"
              sx={{ color: 'inherit' }}
            >
              <OpenInNewIcon fontSize="small" sx={{ color: colors.info }} />
              <Typography fontSize="13px" sx={{ wordBreak: 'break-all' }}>
                {resource.label}
              </Typography>
            </Box>
            {onLinkDelete && (
              <Tooltip title={t('Delete')}>
                <IconButton
                  size="small"
                  onClick={() => onLinkDelete(resource)}
                  sx={{
                    color: colors.error,
                    ':hover': { backgroundColor: `${colors.error}20` },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}

        {isAdding ? (
          // clicking away discards the entry, only the button and Enter save
          <ClickAwayListener onClickAway={handleClose}>
            <Box
              className="flex items-center gap-2 rounded-lg px-2 py-1"
              sx={{
                backgroundColor: colors.bgInput,
                border: `1px solid ${colors.info}80`,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
                if (e.key === 'Escape') handleClose();
              }}
            >
              <AddLinkIcon fontSize="small" sx={{ color: colors.info }} />
              <InputBase
                autoFocus
                value={newLink.label}
                placeholder={t('Label')}
                onChange={(e) =>
                  setNewLink({ ...newLink, label: e.target.value })
                }
                sx={{
                  fontSize: '13px',
                  width: '35%',
                  paddingX: '8px',
                  borderRight: `1px solid ${colors.text}20`,
                }}
              />
              <InputBase
                fullWidth
                value={newLink.url}
                placeholder="https://"
                onChange={(e) =>
                  setNewLink({ ...newLink, url: e.target.value })
                }
                sx={{ fontSize: '13px', paddingX: '4px' }}
              />
              <Tooltip title={t('Save')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!newLink.url.trim()}
                    onClick={handleSubmit}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: colors.info,
                      color: colors.text,
                      ':hover': { backgroundColor: colors.info },
                      '&.Mui-disabled': {
                        backgroundColor: `${colors.info}40`,
                      },
                    }}
                  >
                    <CheckIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </ClickAwayListener>
        ) : (
          <DashedRow onClick={() => setIsAdding(true)}>
            <AddLinkIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
            <Typography fontSize="12px" className="opacity-60">
              {t('Add link')}
            </Typography>
          </DashedRow>
        )}
      </Box>
    </Box>
  );
};

export default TaskCardLinks;
